import { createEmgAuth, type EmgAuthClaims, type EmgAuthClient } from '@emg/auth-client';

import { authConfig, usesSso } from './authConfig';

/**
 * EAA-1694: the editor's single OIDC session.
 *
 * Behaviour comes from the shared `@emg/auth-client` (EAA-1730) — the one source of truth
 * for login/renew/logout/session-end across portal, web-content and the LMS. The editor
 * adds no OIDC logic of its own; a second implementation is exactly the drift that package
 * exists to prevent.
 *
 * Module-level singleton so there is exactly one PKCE/session state per tab: `main.tsx`'s
 * `configureApi` header builder, the auth provider and the callback route all read through
 * this instance.
 */
export type { EmgAuthClaims, EmgAuthClient };

/**
 * In `token` mode there is no Keycloak to talk to. `createEmgAuth`'s `UserManager` calls
 * `getUser()` on construction and may then fetch the authority's
 * `.well-known/openid-configuration` — a real network call on every page load, which in a
 * Keycloak-less environment is a console full of failed fetches for no benefit (the same
 * problem web-content hit in UAT). So the client is not constructed at all, and every
 * method throws rather than pretending to succeed: in `token` mode these are unreachable.
 */
function createUnusedAuthClient(): EmgAuthClient {
    const unreachable = (): never => {
        throw new Error(
            'authClient is unused in token mode (VITE_AUTH_MODE=token) — this call path should not be reachable.'
        );
    };
    return {
        login: unreachable,
        handleCallback: unreachable,
        getAccessToken: () => Promise.resolve(null),
        getAccessTokenSync: () => null,
        getClaims: () => Promise.resolve(null),
        refreshToken: () => Promise.resolve(null),
        logout: () => Promise.resolve(),
        onSessionEnded: () => () => undefined,
        capabilityResolver: () => ({ can: () => false }),
    };
}

export const authClient: EmgAuthClient = usesSso
    ? createEmgAuth({
          authority: authConfig.authority,
          clientId: authConfig.clientId,
          redirectUri: `${window.location.origin}/callback`,
      })
    : createUnusedAuthClient();
